import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import WorkIcon from '@mui/icons-material/Work';
import { Box } from '@mui/material';

import MicrositeImages from '~/components/atoms/microsite-images/MicrositeImages';
import { getVCardPlusReducer } from '~/store/reducers/previewScreens/vcard-plus/selectors';
import { COLORS } from '~/styles/colors';
import { mainPreviewContainerStyles } from '~/styles/common';
import { formatAddress } from '~/utils/common';
import { getIconPhoneByType } from '~/utils/getIconPhone';

import { downloadContacts } from '../service';
import { getIconByType } from '../utils';

import VCardPreviewAddContactButton from './VCardPreviewAddContactButton';
import VCardPreviewAvatar from './VCardPreviewAvatar';
import VCardPreviewContactInfo from './VCardPreviewContactInfo';
import VCardPreviewFloatingAddContactBtn from './VCardPreviewFloatingAddContactBtn';
import VCardPreviewListItem from './VCardPreviewListItem';
import VCardPreviewLocation from './VCardPreviewLocation';
import VCardPreviewSocialBlock from './VCardPreviewSocialBlock';
import VCardPreviewSummary from './VCardPreviewSummary';
import VCardPreviewTitleAndBodyTexts from './VCardPreviewTitleAndBodyTexts';

type VCARD_PLUS_TEMPLATE_0Props = {
  isMyMicrosite?: boolean;
  isAnonymous?: boolean;
  vCardQrData?: any; // TODO replace with real type
};
export function VCARD_PLUS_TEMPLATE_0({ isMyMicrosite, isAnonymous, vCardQrData }: VCARD_PLUS_TEMPLATE_0Props) {
  const vCardPlusState = useSelector(getVCardPlusReducer);
  const sectionFirst = useRef(null);
  const sectionSecond = useRef(null);
  const sectionThird = useRef(null);

  const scrollToSection = (item: 'phone' | 'email' | 'geo') => {
    switch (item) {
      case 'phone':
        return sectionFirst.current && sectionFirst.current.scrollIntoView({ behavior: 'smooth' });
      case 'email':
        return sectionSecond.current && sectionSecond.current.scrollIntoView({ behavior: 'smooth' });
      case 'geo':
        return sectionThird.current && sectionThird.current.scrollIntoView({ behavior: 'smooth' });
      default:
        return null;
    }
  };

  const {
    bacgkroundColor,
    fonts: { title, text },
    content,
    content: { socialTitle, footerTitle, footerType },
    micrositeStyles,
    micrositeId,
    micrositeData,
  } = vCardPlusState;

  const {
    images = [],
    imagesViewType,
    location = {},
    person: {
      firstName: name,
      lastName: surName,
      title: titleValue,
      photo: { scale, borderRadius, rotate, logo: url } = {},
    } = {},
    personTitle: titlePosition,
    summary = '',
    phones = [],
    emails = [],
    websites = [],
    companies = [],
    socials = [],
  } = (isAnonymous ? vCardQrData : micrositeData) || {};

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = summary;
  const textContent = tempDiv.textContent.trim();

  const [coordinates, setCoordinates] = useState(null);
  const hasFullAddress = [
    location.street,
    location.number,
    location.postalCode,
    location.city,
    location.state,
    location.country,
  ].every(Boolean);

  const hasCountryAndCity = !!location.country && !!location.city;
  const hasUrl = !!location.url;
  const hasCoordinates = !!location.latitude && !!location.longitude;

  const isShowLocation = hasFullAddress || hasCountryAndCity || hasUrl || hasCoordinates;

  const addressString = formatAddress(location);

  const geocodeAddress = async (address) => {
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        setCoordinates({
          lat: location.lat(),
          lng: location.lng(),
        });
      }
    });
  };

  useEffect(() => {
    if (addressString) {
      const timer = setTimeout(() => {
        geocodeAddress(addressString);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [addressString]);

  const handleClickAddContact = () => {
    void downloadContacts(vCardPlusState?.micrositeData?.id || micrositeId);
  };

  return (
    <Box
      sx={[
        {
          ...mainPreviewContainerStyles,
          alignItems: 'normal',
          display: 'flex',
          flexDirection: 'column',
          ...micrositeStyles.container,
          position: 'relative',
        },
        isMyMicrosite && {
          margin: 0,
          minHeight: '100vh',
          borderRadius: 0,
        },
      ]}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', paddingBottom: '12px' }}>
        <Box
          sx={[
            {
              display: 'flex',
              flexDirection: 'column',
              background: bacgkroundColor[0],
              padding: '16px',
              margin: isMyMicrosite ? undefined : '0 2px',
              borderRadius: isMyMicrosite ? undefined : '30px 30px 0 0',
            },
            {
              margin: isAnonymous ? '0 4px' : undefined,
              borderRadius: isAnonymous ? '42px 42px 0 0' : undefined,
            },
          ]}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: 2,
            }}
          >
            {!!url && <VCardPreviewAvatar borderRadius={borderRadius} scale={scale} rotate={rotate} avatar={url} />}
          </Box>
          <VCardPreviewTitleAndBodyTexts
            text={text}
            title={title}
            name={name}
            surName={surName}
            titlePosition={titlePosition}
            titleValue={titleValue}
          />
          <VCardPreviewContactInfo
            content={content}
            backgroundColor={bacgkroundColor[1]}
            scrollToSection={scrollToSection}
            isAnonymous={isAnonymous}
          />
        </Box>

        {footerTitle && footerType === 'top' && !isAnonymous && (
          <VCardPreviewAddContactButton
            label={footerTitle}
            backgroundColor={bacgkroundColor[1]}
            onClick={handleClickAddContact}
          />
        )}
        {!!textContent && (
          <VCardPreviewSummary summary={summary} rounded={false} isMarginHorizontal isAnonymous={isAnonymous} />
        )}
        <Box ref={sectionFirst}>
          {phones?.map((item) => {
            return (
              <VCardPreviewListItem
                key={item.id}
                Icon={getIconPhoneByType(item.type.toUpperCase(), 16)}
                linkHref={`tel:${item.phone}`}
                value={item.phone}
                label={item.title}
                isBorder
                rounded={false}
                isMarginHorizontal
                isAnonymous={isAnonymous}
              />
            );
          })}
        </Box>
        <Box ref={sectionSecond}>
          {emails?.map((item) => {
            return (
              <VCardPreviewListItem
                key={item.id}
                Icon={getIconByType('email', COLORS.black06)}
                linkHref={`mailto:${item.email}`}
                value={item.email}
                label={item.title}
                isBorder
                rounded={false}
                isMarginHorizontal
                isAnonymous={isAnonymous}
              />
            );
          })}
        </Box>
        {websites.map((item) => {
          return (
            <VCardPreviewListItem
              key={item.id}
              Icon={getIconByType('website', COLORS.black06)}
              linkHref={item.url}
              value={item.url}
              label={item.title}
              isMarginHorizontal
              isBorder
              rounded={false}
              target="_blank"
              rel="noopener noreferrer"
              isAnonymous={isAnonymous}
            />
          );
        })}
        {isShowLocation && (
          <VCardPreviewLocation
            addressString={addressString}
            coordinates={coordinates}
            location={location}
            ref={sectionThird}
            isBorder
            rounded={false}
            isMarginHorizontal
            isAnonymous={isAnonymous}
          />
        )}
        {companies.map((item) => {
          return (
            <VCardPreviewListItem
              key={item.name}
              Icon={<WorkIcon sx={{ color: COLORS.black06, width: '16px', height: '16px' }} />}
              value={item.name}
              label={item.profession}
              isBorder
              rounded={false}
              isMarginHorizontal
              isAnonymous={isAnonymous}
            />
          );
        })}
        {!!socials?.length && (
          <VCardPreviewSocialBlock
            socialTitle={socialTitle}
            socialLinks={socials}
            rounded={false}
            isBorder
            isAnonymous={isAnonymous}
          />
        )}
        {!!images?.length && (
          <Box sx={{ padding: '8px 10px' }}>{<MicrositeImages data={images} type={imagesViewType} />}</Box>
        )}
        {footerTitle && footerType === 'floating' && !isAnonymous && (
          <VCardPreviewFloatingAddContactBtn backgroundColor={bacgkroundColor[1]} onClick={handleClickAddContact} />
        )}
        {footerTitle && footerType === null && !isAnonymous && (
          <VCardPreviewAddContactButton
            label={footerTitle}
            backgroundColor={bacgkroundColor[1]}
            onClick={handleClickAddContact}
          />
        )}
      </Box>
    </Box>
  );
}
